import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Zap, ArrowRight } from "lucide-react";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col p-8 lg:p-16">
        <Link to="/" className="flex items-center gap-2" data-testid="register-logo-link">
          <div className="w-8 h-8 bg-zinc-950 rounded-md flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#FF4F00]" strokeWidth={2.5} />
          </div>
          <span className="font-extrabold text-lg tracking-tight">MigrateFlow</span>
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
              New Operator
            </p>
            <h1 className="text-4xl font-extrabold tracking-tight mb-2">Create account</h1>
            <p className="text-zinc-600 mb-10">Set up your control room in under a minute.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div>
                <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Name</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Work email</label>
                <input
                  data-testid="register-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Password</label>
                <input
                  data-testid="register-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 transition-all"
                />
              </div>
              <button
                data-testid="register-submit-button"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-[.98]"
              >
                {busy ? "Creating…" : "Create account"}
                {!busy && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <p className="mt-8 text-sm text-zinc-600">
              Already have an account?{" "}
              <Link to="/login" data-testid="register-to-login-link" className="font-semibold text-zinc-950 underline underline-offset-4">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-zinc-950">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjBzdHJlYW0lMjBuZXR3b3JrfGVufDB8fHx8MTc4MjkxNTY2OXww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 tech-grid-dark" />
        <div className="relative h-full flex flex-col justify-end p-16 text-white">
          <p className="font-mono text-xs uppercase tracking-widest text-[#FF4F00]">
            $ migrateflow --new-tenant
          </p>
          <p className="mt-3 font-extrabold text-4xl tracking-tight leading-tight max-w-md">
            Every folder, every flag, every message. Right where it belongs.
          </p>
        </div>
      </div>
    </div>
  );
}
